import { AlertCircle, RefreshCw, SlidersHorizontal } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '~/components/common/confirm-dialog';
import { DataTablePagination } from '~/components/common/data-table/data-table-pagination';
import { Button } from '~/components/ui/button';
import { Sheet, SheetContent, SheetTitle } from '~/components/ui/sheet';
import {
  useCatalogProducts,
  useDeleteSyncProduct,
  useInfiniteCatalogProducts,
  useProductColumns,
  useProductFilterCatalog,
  useProductSearchFields,
} from '~/features/explorer/hooks/use-products-catalog';
import type {
  ProductAppliedFilters,
  ProductFilterState,
  ProductViewDensity,
  ProductViewMode,
  ProductsListRequest,
  ProductsListSearch,
  SyncCapability,
} from '~/features/explorer/types/products-catalog.types';
import {
  DEFAULT_FILTER_STATE,
  PRODUCT_PAGE_SIZE_OPTIONS,
  buildProductsFilterEnvelope,
  getProductField,
  isCapabilitySupported,
} from '~/features/explorer/utils/products-catalog';
import { useDebouncedValue } from '~/hooks/use-debounced-value';

import { ProductActiveFilters } from './product-active-filters';
import { ProductDetailDrawer } from './product-detail-drawer';
import { ProductEditDialog } from './product-edit-dialog';
import { ProductFacetSidebar } from './product-facet-sidebar';
import {
  ProductFilterBar,
  type ColumnVisibility,
  type ProductColumnOption,
} from './product-filter-bar';
import { ProductGrid } from './product-grid';
import { ProductsTable } from './products-table';
import { SearchFieldSelect } from './search-field-select';

type Product = Record<string, unknown>;

/** Dynamic applied-filter map (filter key → chosen value), passed to the
 *  products request `filters` envelope verbatim. Shared shape used by both the
 *  dynamic drawer and the sidebar facet layout. */
type AppliedFilters = ProductAppliedFilters;

/** Column codes shown when the connector column metadata isn't yet available. */
const FALLBACK_COLUMN_CODES = [
  'title',
  'brand',
  'source_status',
  'availability',
  'price_amount',
  'data_quality_score',
  'data_quality_grade',
  'critical_issues',
];

/** Default rows-per-page — the largest allowed option so the catalog opens
 *  with the fullest page. 100 is the backend's page-size cap (PaginationParams
 *  `le=100`). Widened to `number` so paging can set any size. */
const DEFAULT_PAGE_SIZE: number = PRODUCT_PAGE_SIZE_OPTIONS.at(-1) ?? 100;

/** Stable per-row key (id-first) for locating a product within the current
 *  page — used to drive panel prev/next without holding a stale index. */
function productKey(p: Product): string {
  for (const v of [p.id, p.externalId, p.offer_id, p.offerId]) {
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return '';
}

/** First primitive id alias on a product row, for the delete external id. */
function externalIdOf(p: Product): string {
  for (const v of [p.externalId, p.offer_id, p.offerId, p.id]) {
    if (typeof v === 'string' && v) return v;
    if (typeof v === 'number') return String(v);
  }
  return '';
}

/**
 * Opt-in customisation of the products table. Omitted entirely by the
 * Explorer → Products page (all defaults); supplied by Product Catalog to
 * get header sorting, truncated cells, a scrollable body, a checkbox column
 * picker, an expanded sort list, and a default-visible column set.
 */
export interface ProductsTabConfig {
  /** Sort dropdown options (card view). Defaults to the shared SORT_OPTIONS. */
  sortOptions?: readonly { value: string; label: string }[];
  /** Column id → backend sort keys. Presence enables header sorting in table
   *  view (and hides the sort dropdown there). */
  headerSortMap?: Record<string, { asc: string; desc: string }>;
  /** Column ids visible by default; all others start hidden. */
  defaultVisibleColumnIds?: string[];
  /** Column-picker layout. Default: `'pills'`. */
  columnPickerVariant?: 'pills' | 'checkbox';
  /** Cap cells at 320px / 3 lines with a hover tooltip. */
  truncateCells?: boolean;
  /** Pin the header and scroll rows within this max-height. */
  tableMaxHeight?: string;
  /** Grow the table to fill the available height instead of a fixed max-height. */
  tableFillHeight?: boolean;
  /** Custom product-detail renderer for the view action. When provided it
   *  replaces the built-in detail dialog (edit/delete keep their own dialogs).
   *  Receives the current page's rows plus the open row's index and navigation
   *  callbacks so a panel can step prev/next. */
  renderDetail?: (args: {
    products: Record<string, unknown>[];
    activeIndex: number | null;
    onClose: () => void;
    onNavigate: (index: number) => void;
    connectionId: string;
    connectorCode: string;
    subsidiaryId: string | undefined;
  }) => ReactNode;
  /** Use server-driven dynamic filters instead of the hardcoded drawer. The
   *  applied filter map then drives the products request `filters` envelope
   *  (keyed by the backend filter keys), and `renderFilterSlot` supplies the UI. */
  dynamicFilters?: boolean;
  /** Field-targeted search: a picker beside the search box chooses the field
   *  (offer id, item group id, MPN, GTIN, title — from `products/search-fields`)
   *  and the term is sent as `search: {field, value}` instead of the title-only
   *  `filters.query`. */
  fieldSearch?: boolean;
  /** Filter control rendered in the toolbar when `dynamicFilters` is on. */
  renderFilterSlot?: (args: {
    applied: AppliedFilters;
    onApply: (next: AppliedFilters) => void;
    connectionId: string;
    connectorCode: string;
    syncId: string | null;
  }) => ReactNode;
  /** Notified whenever the applied filters change (dynamic-filters mode) so the
   *  parent can react (e.g. surface a create-batch prompt). */
  onAppliedFiltersChange?: (applied: AppliedFilters) => void;
  /** Notified with the server's total row count for the current scope, so a
   *  parent can state how many SKUs an action will cover. Fires on the filtered
   *  total, not the revision's product count. */
  onTotalCountChange?: (total: number) => void;
  /** Opt-in: notified when the catalog is genuinely empty — zero products with no
   *  active filter or search (a filtered-to-zero result does NOT count). Providing
   *  this also hides the filter/search strip in that state, since there is nothing
   *  to search or filter. Lets the parent disable actions like "Create batch". */
  onCatalogEmptyChange?: (isCatalogEmpty: boolean) => void;
  /** Notified while the products list is loading (initial fetch), so a parent can
   *  disable actions like "Create batch" until the catalog has resolved. */
  onLoadingChange?: (isLoading: boolean) => void;
  /** Override the card-grid responsive column breakpoints (e.g.
   *  `grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4`). Defaults to 2/3/4. */
  cardGridClassName?: string;
}

interface ProductsTabProps {
  connectionId: string;
  connectorCode: string;
  syncId: string | null;
  subsidiaryId?: string;
  capabilities: SyncCapability[];
  /** Opt-in table customisation (see ProductsTabConfig). */
  config?: ProductsTabConfig;
  /** Filter presentation: `'drawer'` (default, right-side sheet) or `'sidebar'`
   *  (left facet panel, server-driven descriptors) for the Catalog Browse layout. */
  filtersLayout?: 'drawer' | 'sidebar';
}

/**
 * Products-tab orchestrator: owns filter/pagination/view state, fetches the
 * paginated catalog, vendor filter universe, and column metadata, and wires the
 * filter bar, grid/table views, pagination, and the detail/edit/delete dialogs.
 */
export function ProductsTab({
  connectionId,
  connectorCode,
  syncId,
  subsidiaryId,
  capabilities,
  config,
  filtersLayout = 'drawer',
}: ProductsTabProps) {
  const { t } = useTranslation('explorer');

  const isSidebar = filtersLayout === 'sidebar';
  // The sidebar layout drives filtering through the server descriptor map (same
  // passthrough envelope as the dynamic drawer), not the hardcoded filter state.
  const usesAppliedMap = !!config?.dynamicFilters || isSidebar;

  const [filters, setFilters] = useState(DEFAULT_FILTER_STATE);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [viewMode, setViewMode] = useState<ProductViewMode>('grid');
  const [viewDensity, setViewDensity] = useState<ProductViewDensity>('comfort');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({});
  // Detail selection is tracked by row key (not index) so paging/sorting can't
  // leave a stale index pointing at a different product.
  const [detailKey, setDetailKey] = useState<string | null>(null);
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Product | null>(null);
  // Server-driven applied filters (dynamic-filters mode only).
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});

  // Reset to page 0 whenever the (external) sync changes — render-time compare
  // avoids the set-state-in-effect anti-pattern.
  const [lastSyncId, setLastSyncId] = useState(syncId);
  if (syncId !== lastSyncId) {
    setLastSyncId(syncId);
    setPage(0);
  }

  const canEdit = isCapabilitySupported(capabilities, 'update');
  const canDelete = isCapabilitySupported(capabilities, 'delete');

  const debouncedSearch = useDebouncedValue(filters.search, 500);

  // Field-targeted search: the picked field falls back to the connector's first
  // option, which also covers a connector switch invalidating the old pick.
  const fieldSearch = !!config?.fieldSearch;
  const searchFieldsQuery = useProductSearchFields(connectorCode, subsidiaryId, fieldSearch);
  const searchFields = useMemo(
    () => searchFieldsQuery.data?.data ?? [],
    [searchFieldsQuery.data?.data],
  );
  const [pickedSearchField, setPickedSearchField] = useState<string | null>(null);
  const activeSearchField =
    searchFields.find((f) => f.key === pickedSearchField) ?? searchFields[0] ?? null;
  const search = useMemo<ProductsListSearch | null>(() => {
    const value = debouncedSearch.trim();
    return fieldSearch && value && activeSearchField
      ? { field: activeSearchField.key, value }
      : null;
  }, [fieldSearch, debouncedSearch, activeSearchField]);

  // Applied-map mode (dynamic drawer or sidebar): pass the filter map straight
  // through (keyed by backend filter keys) plus the free-text query. Otherwise
  // use the hardcoded envelope. In field-search mode the term rides on `search`
  // instead, so it is kept out of the envelope.
  const filterEnvelope = useMemo(() => {
    const freeText = fieldSearch ? '' : debouncedSearch;
    if (usesAppliedMap) {
      const env: Record<string, unknown> = { ...appliedFilters };
      const query = freeText.trim();
      if (query) env.query = query;
      return env;
    }
    return buildProductsFilterEnvelope({ ...filters, search: freeText });
  }, [usesAppliedMap, appliedFilters, filters, debouncedSearch, fieldSearch]);

  // Infinite (append-on-scroll) card view — only the integrations sidebar
  // layout. Table view, and the other (drawer-layout) consumer's grid, keep the
  // paginated query with page controls. Only the active view's query is enabled.
  const isGrid = viewMode === 'grid';
  const useInfinite = isGrid && isSidebar;
  const sortBy = filters.sortBy || 'score_desc';

  const request: ProductsListRequest = {
    connectionId,
    syncId: syncId ?? null,
    offset: page * pageSize,
    limit: pageSize,
    sortBy,
    filters: filterEnvelope,
    search,
  };
  const paginatedQuery = useCatalogProducts(request, subsidiaryId, !!connectionId && !useInfinite);
  const infiniteQuery = useInfiniteCatalogProducts(
    { connectionId, syncId: syncId ?? null, sortBy, filters: filterEnvelope, search },
    pageSize,
    subsidiaryId,
    !!connectionId && useInfinite,
  );
  const listQuery = useInfinite ? infiniteQuery : paginatedQuery;

  // Rows are consumed as loose records (field getters read many aliases); the
  // typed `ProductListItem` interface lacks an index signature so cast to Product.
  const products = useMemo<Product[]>(() => {
    const rows = useInfinite
      ? (infiniteQuery.data?.pages.flatMap((pg) => pg.data.products) ?? [])
      : (paginatedQuery.data?.data.products ?? []);
    return rows as unknown as Product[];
  }, [useInfinite, infiniteQuery.data, paginatedQuery.data]);
  const totalProducts = useInfinite
    ? (infiniteQuery.data?.pages[0]?.data.pagination.total ?? 0)
    : (paginatedQuery.data?.data.pagination.total ?? 0);

  // Surface the scope size to the parent. Effect rather than an inline call so
  // the notification happens after commit and can't re-render mid-render.
  const onTotalCountChange = config?.onTotalCountChange;
  useEffect(() => {
    onTotalCountChange?.(totalProducts);
  }, [onTotalCountChange, totalProducts]);

  // Resolve the open product from its key against the current page. When the
  // key isn't on this page (paged away), the detail is treated as closed.
  const detailIndex =
    detailKey === null ? -1 : products.findIndex((p) => productKey(p) === detailKey);
  const viewProduct = detailIndex >= 0 ? (products[detailIndex] ?? null) : null;
  const openDetail = (row: Product) => {
    setDetailKey(productKey(row));
  };

  // Vendor options come from the brand descriptor — fetched with `brand`
  // removed from the applied filters so the universe stays complete. In sidebar
  // mode we fetch the full descriptor set with an empty applied map so the facet
  // option universe stays stable as selections are made.
  const appliedFiltersForCatalog = useMemo(() => {
    if (isSidebar) return {};
    const rest: Record<string, unknown> = { ...filterEnvelope };
    delete rest.brand;
    return rest;
  }, [isSidebar, filterEnvelope]);
  const filterCatalogQuery = useProductFilterCatalog(
    {
      connectorCode,
      connectionId,
      syncId: syncId ?? null,
      appliedFilters: appliedFiltersForCatalog,
    },
    subsidiaryId,
    // Fetch for the hardcoded drawer (vendor options) and the sidebar (full
    // descriptors); the dynamic drawer fetches its own definitions, so skip it.
    (isSidebar || !config?.dynamicFilters) && !!connectionId && !!connectorCode,
  );
  const vendorOptions = useMemo(
    () => filterCatalogQuery.data?.data.find((f) => f.key === 'brand')?.options ?? [],
    [filterCatalogQuery.data],
  );
  // Full descriptor list for the sidebar facets.
  const facetDescriptors = useMemo(
    () => filterCatalogQuery.data?.data ?? [],
    [filterCatalogQuery.data],
  );

  const columnsQuery = useProductColumns(connectorCode, subsidiaryId, !!connectorCode);
  const apiColumns = useMemo(() => columnsQuery.data?.data ?? [], [columnsQuery.data?.data]);

  const fallbackLabels: Record<string, string> = {
    title: t('products.colProduct'),
    brand: t('products.colVendor'),
    source_status: t('products.colStatus'),
    availability: t('products.colStock'),
    price_amount: t('products.price'),
    data_quality_score: t('products.score'),
    data_quality_grade: t('products.colQuality'),
    critical_issues: t('products.colIssues'),
  };
  const tableColumns = useMemo<ProductColumnOption[]>(() => {
    const source =
      apiColumns.length > 0
        ? apiColumns.map((c) => ({ id: c.code, label: c.label }))
        : FALLBACK_COLUMN_CODES.map((code) => ({ id: code, label: fallbackLabels[code] ?? code }));
    // Pin `title` first to match the table's column order.
    return [...source.filter((c) => c.id === 'title'), ...source.filter((c) => c.id !== 'title')];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fallbackLabels is derived from stable t
  }, [apiColumns]);

  // When a default-visible set is supplied, any column not yet explicitly set in
  // the visibility map is visible only if it's in that set — so the table starts
  // with just those columns and the rest stay toggleable via the picker.
  const effectiveColumnVisibility = useMemo<ColumnVisibility>(() => {
    const ids = config?.defaultVisibleColumnIds;
    if (!ids) return columnVisibility;
    const next: ColumnVisibility = { ...columnVisibility };
    for (const col of tableColumns) {
      if (!(col.id in next)) next[col.id] = ids.includes(col.id);
    }
    return next;
  }, [columnVisibility, tableColumns, config?.defaultVisibleColumnIds]);

  const isLoading = listQuery.isLoading;
  const isFetching = listQuery.isFetching && !listQuery.isLoading;

  // The catalog is genuinely empty only when there are zero rows AND nothing is
  // scoping them out — a filtered/searched-to-zero result is not "empty" (the
  // strip must stay so the user can clear it). Reported up (and drives the
  // strip-hide) only for consumers that opt in via `onCatalogEmptyChange`.
  const hasActiveScope = Object.keys(appliedFilters).length > 0 || filters.search.trim().length > 0;
  const isCatalogEmpty = !isLoading && totalProducts === 0 && !hasActiveScope;
  const onCatalogEmptyChange = config?.onCatalogEmptyChange;
  useEffect(() => {
    onCatalogEmptyChange?.(isCatalogEmpty);
  }, [onCatalogEmptyChange, isCatalogEmpty]);
  const onLoadingChange = config?.onLoadingChange;
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [onLoadingChange, isLoading]);

  const deleteMutation = useDeleteSyncProduct(subsidiaryId);
  const totalPages = Math.max(1, Math.ceil(totalProducts / pageSize));

  function handleFiltersChange(next: ProductFilterState) {
    setFilters(next);
    setPage(0);
  }

  function handleGridPageChange(next: number) {
    setPage(next - 1);
  }

  function handlePageSizeChange(size: number) {
    setPageSize(size);
    setPage(0);
  }

  // Scroll the list back to the top whenever the committed filters / search /
  // sort change, so a change never leaves the user mid-scroll on stale results.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const filterKey = `${JSON.stringify(filterEnvelope)}|${sortBy}`;
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [filterKey]);

  // Infinite-scroll sentinel — fetch the next card page as it scrolls into view.
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = infiniteQuery;
  useEffect(() => {
    if (!useInfinite) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      },
      { rootMargin: '300px' },
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [useInfinite, products.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    const externalId = externalIdOf(deleteTarget);
    deleteMutation.mutate(
      { connectionId, externalId },
      {
        onSuccess: () => {
          setDeleteTarget(null);
        },
      },
    );
  }

  // Applied-map filter changes (dynamic drawer + sidebar) — resets to page 0.
  function handleAppliedChange(next: AppliedFilters) {
    setAppliedFilters(next);
    setPage(0);
    config?.onAppliedFiltersChange?.(next);
  }

  // Dynamic filter control (server-driven drawer) — applying resets to page 0.
  const filterSlot = config?.dynamicFilters
    ? config.renderFilterSlot?.({
        applied: appliedFilters,
        onApply: handleAppliedChange,
        connectionId,
        connectorCode,
        syncId,
      })
    : undefined;

  // Hide the strip when the opted-in consumer's catalog is genuinely empty —
  // there is nothing to search, filter, or sort.
  const hideStrip = !!onCatalogEmptyChange && isCatalogEmpty;
  const filterBar = !hideStrip && (!isLoading || products.length > 0) && (
    <ProductFilterBar
      filters={filters}
      onChange={handleFiltersChange}
      filterSlot={filterSlot}
      vendorOptions={vendorOptions}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      disabled={isLoading}
      totalCount={totalProducts}
      tableColumns={tableColumns}
      columnVisibility={effectiveColumnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      hideSortInTableView={!!config?.headerSortMap}
      {...(config?.sortOptions ? { sortOptions: config.sortOptions } : {})}
      {...(config?.columnPickerVariant ? { columnPickerVariant: config.columnPickerVariant } : {})}
      {...(isSidebar
        ? { hideFilterButton: true, viewDensity, onViewDensityChange: setViewDensity }
        : {})}
      {...(fieldSearch && activeSearchField
        ? {
            searchFieldSlot: (
              <SearchFieldSelect
                fields={searchFields}
                value={activeSearchField.key}
                onChange={(key) => {
                  setPickedSearchField(key);
                  setPage(0);
                }}
                disabled={isLoading}
              />
            ),
            searchPlaceholder: t('products.fieldSearchPlaceholder', {
              field: activeSearchField.label,
            }),
          }
        : {})}
    />
  );

  // Grid sizing: the sidebar (Catalog Browse) layout uses the density presets;
  // other consumers can pass a fixed column override via `config.cardGridClassName`.
  const gridSizingProps: { density?: ProductViewDensity; gridClassName?: string } = isSidebar
    ? { density: viewDensity }
    : {};
  if (!isSidebar && config?.cardGridClassName) {
    gridSizingProps.gridClassName = config.cardGridClassName;
  }

  const content = (
    <div ref={scrollRef} className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto">
      {listQuery.isError ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed py-16 text-center">
          <div className="bg-destructive/10 mb-3 rounded-full p-4">
            <AlertCircle className="text-destructive h-8 w-8" />
          </div>
          <p className="text-sm font-medium">{t('products.errorTitle')}</p>
          <p className="text-muted-foreground mt-1 max-w-sm text-xs">{t('products.errorBody')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4 cursor-pointer"
            isLoading={listQuery.isFetching}
            onClick={() => {
              void listQuery.refetch();
            }}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            {t('products.tryAgain')}
          </Button>
        </div>
      ) : viewMode === 'table' ? (
        <ProductsTable
          products={products}
          capabilities={capabilities}
          apiColumns={apiColumns}
          isLoading={isLoading}
          columnVisibility={effectiveColumnVisibility}
          page={page}
          pageSize={pageSize}
          totalRows={totalProducts}
          onPaginationChange={(pageIndex, size) => {
            setPage(pageIndex);
            setPageSize(size);
          }}
          onEdit={setEditProduct}
          onDelete={setDeleteTarget}
          onRowClick={openDetail}
          truncateCells={!!config?.truncateCells}
          maxHeight={config?.tableMaxHeight}
          fillHeight={!!config?.tableFillHeight}
          {...(config?.headerSortMap
            ? {
                headerSortMap: config.headerSortMap,
                sortBy: filters.sortBy,
                onSortByChange: (v: string) => {
                  handleFiltersChange({ ...filters, sortBy: v });
                },
              }
            : {})}
        />
      ) : (
        <>
          <ProductGrid
            products={products}
            capabilities={capabilities}
            isLoading={isLoading}
            isFetching={isFetching}
            onEdit={setEditProduct}
            onDelete={setDeleteTarget}
            onRowClick={openDetail}
            loadingMore={useInfinite && infiniteQuery.isFetchingNextPage}
            {...gridSizingProps}
          />
          {useInfinite
            ? /* Infinite scroll: sentinel loads the next page as it enters view;
                 loading placeholders render as skeleton cards within the grid. */
              !isLoading &&
              products.length > 0 && (
                <div ref={loadMoreRef} className="flex items-center justify-center py-4">
                  {!infiniteQuery.hasNextPage && !infiniteQuery.isFetchingNextPage && (
                    <span className="text-muted-foreground text-xs">
                      {t('products.allLoaded', { count: totalProducts })}
                    </span>
                  )}
                </div>
              )
            : !isLoading &&
              products.length > 0 &&
              totalProducts > pageSize && (
                <DataTablePagination
                  page={page + 1}
                  totalPages={totalPages}
                  totalRows={totalProducts}
                  pageSize={pageSize}
                  pageSizeOptions={[...PRODUCT_PAGE_SIZE_OPTIONS]}
                  onPageChange={handleGridPageChange}
                  onPageSizeChange={handlePageSizeChange}
                  tableItemType={t('products.rowLabel')}
                />
              )}
        </>
      )}
    </div>
  );

  // Left facet panel — reused in the desktop column and the mobile sheet.
  const facetSidebar = (
    <ProductFacetSidebar
      descriptors={facetDescriptors}
      applied={appliedFilters}
      onChange={handleAppliedChange}
      isLoading={filterCatalogQuery.isLoading}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      {isSidebar ? (
        <div className="flex min-h-0 min-w-0 flex-1 gap-6">
          {/* Facet sidebar (desktop) */}
          <div className="hidden w-60 shrink-0 overflow-y-auto pr-1 lg:block">{facetSidebar}</div>
          <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
            {/* Mobile filters trigger */}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setMobileFiltersOpen(true);
              }}
              className="h-10 cursor-pointer justify-center gap-1.5 lg:hidden"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden="true" />
              {t('products.filters')}
            </Button>
            {filterBar}
            {/* Selected-filter chips — the row fills as facets are picked. */}
            <ProductActiveFilters
              descriptors={facetDescriptors}
              applied={appliedFilters}
              onChange={handleAppliedChange}
            />
            {content}
          </div>
        </div>
      ) : (
        <>
          {filterBar}
          {content}
        </>
      )}

      {isSidebar && (
        <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
          <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto p-4">
            <SheetTitle className="mb-4 text-base font-semibold">
              {t('products.filters')}
            </SheetTitle>
            {facetSidebar}
          </SheetContent>
        </Sheet>
      )}

      {config?.renderDetail ? (
        config.renderDetail({
          products,
          activeIndex: detailIndex >= 0 ? detailIndex : null,
          onClose: () => {
            setDetailKey(null);
          },
          onNavigate: (index) => {
            const row = products[index];
            if (row) setDetailKey(productKey(row));
          },
          connectionId,
          connectorCode,
          subsidiaryId,
        })
      ) : (
        <ProductDetailDrawer
          open={detailIndex >= 0}
          onOpenChange={(open) => {
            if (!open) setDetailKey(null);
          }}
          connectionId={connectionId}
          subsidiaryId={subsidiaryId}
          row={viewProduct}
          canEdit={canEdit}
          canDelete={canDelete}
          onEdit={(p) => {
            setDetailKey(null);
            setEditProduct(p);
          }}
          onDelete={(p) => {
            setDetailKey(null);
            setDeleteTarget(p);
          }}
          {...(detailIndex >= 0
            ? {
                index: useInfinite ? detailIndex : page * pageSize + detailIndex,
                total: totalProducts,
                canPrev: detailIndex > 0,
                canNext: detailIndex < products.length - 1,
                onPrev: () => {
                  const prev = products[detailIndex - 1];
                  if (prev) setDetailKey(productKey(prev));
                },
                onNext: () => {
                  const next = products[detailIndex + 1];
                  if (next) setDetailKey(productKey(next));
                },
              }
            : {})}
        />
      )}

      <ProductEditDialog
        open={!!editProduct}
        onOpenChange={(open) => {
          if (!open) setEditProduct(null);
        }}
        product={editProduct}
        connectionId={connectionId}
        subsidiaryId={subsidiaryId}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setDeleteTarget(null);
        }}
        title={t('products.deleteTitle')}
        description={
          <>
            {t('products.deleteConfirmPrefix')}{' '}
            <strong>
              {deleteTarget
                ? getProductField(deleteTarget, 'title', 'name', 'id')
                : t('products.thisProduct')}
            </strong>
            {t('products.deleteConfirmSuffix')}
          </>
        }
        confirmLabel={t('products.delete')}
        isPending={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
